'use client';
import React, { PropsWithChildren, useContext, useEffect } from 'react';
import {
  UserProductComponentType,
  UserProps,
} from '@osmosis/shared/types/user';

import useGeolocation from '../hooks/useGeolocation';
import { LanguageCode } from '@osmosis/shared/languages';
import { StoreType } from '@osmosis/shared/products';
import { IntlProvider } from 'react-intl';
import { IntlMessagesType } from '../types/poeditor';
import { useUserContext } from './UserContext';
import { useQuery } from 'react-query';
import { getCookie, setCookie } from 'cookies-next';
import { USER_LANGUAGE_COOKIE } from '@elsevier-health/osmosis-shared/constants';
import { getLanguage } from '../lib/localization/locale';

export type LanguageContextProps = {
  activeLanguage: LanguageCode;
  bestLanguage?: LanguageCode;
  userLanguage: LanguageCode;
  isEnglish: boolean;
  isUSA: boolean;
  storeType: StoreType;
};

const ENGLISH_CODE = LanguageCode.EN;

const LanguageContext = React.createContext<LanguageContextProps>({
  activeLanguage: ENGLISH_CODE,
  userLanguage: ENGLISH_CODE,
  isEnglish: true,
  isUSA: true,
  storeType: StoreType.US,
});

const handleLanguageCookie = (
  user?: UserProps | null,
  isUserLoaded?: boolean,
  shouldReload?: boolean,
) => {
  if (!isUserLoaded) return;
  try {
    // Get existing cookie language
    const existingCookieLanguage = getCookie(USER_LANGUAGE_COOKIE);

    const acceptedLanguage = navigator?.language || navigator?.languages?.[0];
    const userLanguage = user?.language;

    // Determine the language from the browser settings and user settings
    const definedLanguage =
      getLanguage(acceptedLanguage, userLanguage) || ENGLISH_CODE;

    setCookie(USER_LANGUAGE_COOKIE, definedLanguage);
    if (
      existingCookieLanguage &&
      existingCookieLanguage !== definedLanguage &&
      shouldReload
    ) {
      window.location.reload();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error handling language cookie:', error);
  }
};

/**
 * Language provider.
 */
export const LanguageProvider = (
  props: PropsWithChildren<{
    bestLanguage?: LanguageCode;
    user?: UserProps;
    serverIntlMessages?: IntlMessagesType;
    hasServerTranslationsFilter?: boolean;
    shouldHandleLanguageCookie?: boolean;
  }>,
) => {
  const {
    children,
    // user,
    bestLanguage,
    serverIntlMessages,
    hasServerTranslationsFilter,
    shouldHandleLanguageCookie,
  } = props;

  const { user, isUserLoaded } = useUserContext();

  const userLanguage = user?.language || ENGLISH_CODE;

  const activeLanguage = (() => {
    if (bestLanguage || !user) return bestLanguage || ENGLISH_CODE;

    if (
      user?.productComponents?.includes(
        UserProductComponentType.LANGUAGE_SELECTION,
      )
    )
      return userLanguage;

    return ENGLISH_CODE;
  })();

  const { isUSA } = useGeolocation(user?.geoLocation?.country_code);

  const { data: intlMessages } = useQuery({
    queryKey: ['translations', activeLanguage],
    queryFn: async () => {
      if (activeLanguage === 'en' || !hasServerTranslationsFilter)
        return serverIntlMessages;

      const response = await fetch(`/api/translations/${activeLanguage}`);

      const messages: IntlMessagesType = (await response.json()) || {};

      // skip creating new intlMessages object to avoid re-rendering
      if (serverIntlMessages) {
        Object.entries(messages).forEach(([key, value]) => {
          serverIntlMessages[key] = value;
        });
        return serverIntlMessages;
      }

      return messages;
    },
    initialData: serverIntlMessages || {},
  });

  const messages = // If the user has language selection
    user?.productComponents?.includes(
      UserProductComponentType.LANGUAGE_SELECTION,
    ) ||
    user?.productCode === 'preonboarding' ||
    !user
      ? intlMessages
      : {};

  const isEnglish = activeLanguage === ENGLISH_CODE;
  let storeType = StoreType.US;
  // If the user's language is set to something other than english OR the user isn't in the US
  if (!isEnglish || !isUSA) {
    storeType = StoreType.NON_US;
  }

  const value = {
    activeLanguage,
    userLanguage,
    isEnglish,
    isUSA,
    storeType,
  };

  useEffect(() => {
    document.documentElement.lang = activeLanguage;
    handleLanguageCookie(user, isUserLoaded, shouldHandleLanguageCookie);
  }, [activeLanguage, isUserLoaded, shouldHandleLanguageCookie, user]);

  return (
    <LanguageContext.Provider value={value}>
      <IntlProvider
        messages={messages}
        locale={activeLanguage}
        defaultLocale="en"
        onError={(e) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            // console.error(e);
          }
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useLanguageContext = (): LanguageContextProps =>
  useContext(LanguageContext);
